/**
 * After a failed submit, bring the user to the first problem.
 *
 * Finds the first rendered field error (any element marked `data-field-error`
 * — Input, Textarea, YesNoField, MultiSelectFormField, CardCheckboxGroupField
 * and the S.T.A.R. fields all mark theirs) in DOM order, scrolls it to the
 * middle of the screen, and puts the cursor in the field it belongs to.
 *
 * Why DOM order rather than react-hook-form's own `shouldFocusError`: RHF can
 * only focus fields it has a ref for, i.e. `register()`ed inputs. Most of these
 * forms are multi-selects, card checkboxes, Yes/No cards and S.T.A.R.
 * textareas fed through `setValue`, so RHF would skip them and jump to some
 * later text box instead. Forms that use this helper should set
 * `shouldFocusError: false` so the two don't fight.
 *
 * Waits two animation frames first: it is usually called from an onInvalid
 * callback, which runs before React has painted the new error messages.
 *
 * `scrollIntoView` scrolls every scrollable ancestor, so this also works
 * inside the signup pages' fixed, internally scrolling column, not just the
 * window.
 */
const FOCUSABLE =
  'textarea:not([disabled]):not([readonly]), input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The field an error belongs to: its nearest `data-field` wrapper, else its parent. */
const fieldOf = (error: HTMLElement) => error.closest<HTMLElement>("[data-field]") ?? error.parentElement;

/** Prefer the first EMPTY text box (the part still to fill in), else the first focusable. */
const focusTargetIn = (field: HTMLElement | null): HTMLElement | null => {
  if (!field) return null;
  const boxes = Array.from(
    field.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea:not([disabled]):not([readonly]), input[type="text"]:not([disabled]):not([readonly]), input:not([type]):not([disabled]):not([readonly]), input[type="email"]:not([disabled]):not([readonly]), input[type="url"]:not([disabled]):not([readonly])',
    ),
  );
  return boxes.find((box) => !box.value.trim()) ?? field.querySelector<HTMLElement>(FOCUSABLE);
};

export const scrollToFirstError = (root?: ParentNode | null) => {
  if (typeof window === "undefined") return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const scope: ParentNode = root ?? document;
      const error = scope.querySelector<HTMLElement>("[data-field-error]");
      if (!error) return;
      const target = focusTargetIn(fieldOf(error));
      (target ?? error).scrollIntoView({ behavior: "smooth", block: "center" });
      // preventScroll: the smooth scroll above already positions it; a plain
      // focus() would jump there instantly and cancel the animation.
      target?.focus({ preventScroll: true });
    }),
  );
};
